import React, { useEffect, useState } from "react";
import Modal from "../shared/Modal";
import { findResumableSession } from "../../hooks/useInterviewSession";

// ── ResumeSessionModal (prompt #3) ─────────────────────────────────────────
// On mount, polls GET /interview-sessions/by-candidate/{id} once. If an
// IN_PROGRESS session is found, prompts the candidate to Continue from
// where they left off OR Restart. Either choice closes the modal and
// invokes the appropriate callback.
//
// The candidateId is read from localStorage (set during candidate auth).
// Mount this component near the top of CandidateLayout so it surfaces
// across any candidate-side page after a crash recovery.
//
// Props:
//   onContinue(session) — called when the user picks Continue. Caller
//                         should `adopt()` the session into useInterviewSession.
//   onRestart(session)  — called when the user picks Restart. Caller
//                         should PATCH status=ABANDONED on the old session
//                         and create a fresh one.

function fmtRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "a moment ago";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function ResumeSessionModal({ onContinue, onRestart }) {
  const [session, setSession] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const candidateId = (() => {
      const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
      return raw ? Number(raw) : null;
    })();
    if (!candidateId) {
      setChecked(true);
      return;
    }
    findResumableSession(candidateId).then((s) => {
      setSession(s);
      setChecked(true);
    });
  }, []);

  if (!checked || !session) return null;

  const totalQuestions = session.totalQuestions || null; // server doesn't expose; UI omits "of N" when absent
  const idx = (session.currentQuestionIdx ?? 0) + 1;

  return (
    <Modal
      open
      onClose={() => setSession(null)}
      closeOnBackdrop={false}
      closeOnEsc={false}
      hideCloseButton
      title="Pick up where you left off?"
      description={`We saved your progress${session.lastHeartbeatAt ? ` ${fmtRelative(session.lastHeartbeatAt)}` : ""}.`}
      size="md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setSession(null);
              onRestart?.(session);
            }}
            className="px-3 py-2 rounded border border-subtle text-sm text-vf-base hover:bg-vf-tint min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
          >
            Start over
          </button>
          <button
            type="button"
            data-autofocus
            onClick={() => {
              setSession(null);
              onContinue?.(session);
            }}
            className="px-4 py-2 rounded bg-vf-primary text-white text-sm font-semibold min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
          >
            Continue
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-vf-base">
        <p className="m-0">
          You were on{" "}
          <strong>question {idx}{totalQuestions ? ` of ${totalQuestions}` : ""}</strong>.
          Recording stopped, but your answers up to that point are still saved.
        </p>
        <p className="m-0 text-xs text-vf-muted">
          <strong>Continue</strong> resumes from the next question. <strong>Start over</strong>
          {" "}begins a fresh attempt — your earlier answers will be discarded.
        </p>
      </div>
    </Modal>
  );
}
